import { Router, Routes } from '@angular/router';
import { inject } from '@angular/core';
import { authGuard } from './core/guards/auth-guard';
import { onboardingGuard } from './core/guards/onboarding-guard';
import { adminGuard } from './core/guards/admin-guard';
import { landingGuard } from './core/guards/landing-guard';
import { managerGuard } from './core/guards/manager-guard';
import { eventMemberGuard } from './core/guards/event-member-guard';
import { eventViewGuard } from './core/guards/event-view-guard';

/**
 * Routage de l'alpha en registre déclaratif.
 *
 * Deux pans sont volontairement absents et NE DOIVENT PAS être rebranchés sans
 * décision explicite (voir docs/adr/0001-registre-declaratif-alpha.md) :
 *  • les retraits — l'application ne déplace aucun argent ;
 *  • les entités permanentes (églises, associations) — hors périmètre alpha,
 *    leur code et leurs Cloud Functions restent en place ;
 */
export const routes: Routes = [
  {
    // Racine : vitrine publique sur le web pour un visiteur sans compte, accueil
    // partout ailleurs. C'est `landingGuard` qui tranche — voir son commentaire.
    path: '',
    pathMatch: 'full',
    loadComponent: () => import('./features/landing/landing').then((m) => m.Landing),
    canActivate: [landingGuard],
  },
  {
    path: 'auth',
    loadChildren: () => import('./features/auth/auth.routes').then((m) => m.AUTH_ROUTES),
  },
  {
    path: 'home',
    loadChildren: () => import('./features/home/home.routes').then((m) => m.HOME_ROUTES),
    canActivate: [authGuard, onboardingGuard],
  },
  {
    path: 'events/new',
    loadComponent: () =>
      import('./features/event/pages/event-create/event-create').then((m) => m.EventCreate),
    canActivate: [authGuard, onboardingGuard],
  },
  {
    path: 'events/:eventId',
    loadChildren: () => import('./features/event/event.routes').then((m) => m.EVENT_ROUTES),
    canActivate: [authGuard, onboardingGuard, eventViewGuard],
  },
  {
    path: 'events/:eventId/contributions',
    loadChildren: () =>
      import('./features/contributions/contributions.routes').then((m) => m.CONTRIBUTIONS_ROUTES),
    canActivate: [authGuard, onboardingGuard, eventMemberGuard],
  },
  {
    path: 'events/:eventId/expenses',
    loadChildren: () =>
      import('./features/expenses/expenses.routes').then((m) => m.EXPENSES_ROUTES),
    canActivate: [authGuard, onboardingGuard, eventMemberGuard],
  },
  {
    path: 'events/:eventId/groups',
    loadChildren: () =>
      import('./features/visibility-groups/visibility-groups.routes').then(
        (m) => m.VISIBILITY_GROUPS_ROUTES
      ),
    canActivate: [authGuard, onboardingGuard, eventMemberGuard, managerGuard],
  },
  {
    path: 'events/:eventId/chat',
    loadChildren: () => import('./features/chat/chat.routes').then((m) => m.CHAT_ROUTES),
    canActivate: [authGuard, onboardingGuard, eventMemberGuard],
  },
  {
    path: 'notifications',
    loadChildren: () =>
      import('./features/notifications/notifications.routes').then((m) => m.NOTIFICATIONS_ROUTES),
    canActivate: [authGuard, onboardingGuard],
  },
  {
    path: 'profile',
    loadChildren: () => import('./features/profile/profile.routes').then((m) => m.PROFILE_ROUTES),
    canActivate: [authGuard, onboardingGuard],
  },
  {
    path: 'settings',
    loadChildren: () => import('./features/more/more.routes').then((m) => m.MORE_ROUTES),
    canActivate: [authGuard, onboardingGuard],
  },
  { path: 'about', redirectTo: 'settings/about', pathMatch: 'full' },
  { path: 'help', redirectTo: 'support', pathMatch: 'full' },
  {
    // PUBLIQUE, sans garde : c'est l'URL d'assistance déclarée sur les stores.
    // Apple et Google vérifient qu'elle s'ouvre, et ils la consultent sans
    // compte — derrière `authGuard`, ils ne verraient qu'un écran de connexion.
    // La même page reste accessible depuis les réglages via /settings/support.
    path: 'support',
    loadComponent: () => import('./features/more/pages/support/support').then((m) => m.Support),
  },
  {
    path: 'admin',
    loadChildren: () => import('./features/admin/admin.routes').then((m) => m.ADMIN_ROUTES),
    canActivate: [authGuard, onboardingGuard, adminGuard],
  },
  {
    path: 'join',
    loadComponent: () =>
      import('./features/event/pages/event-join/event-join').then((m) => m.EventJoin),
  },
  {
    // URL de suppression de compte déclarée à Google Play. Publique par
    // nécessité : elle s'adresse aussi à quelqu'un qui ne parvient plus à se
    // connecter, et Google exige qu'elle expose la procédure, ce qui est
    // effacé, ce qui est conservé et pour combien de temps.
    path: 'suppression-compte',
    loadComponent: () =>
      import('./features/account-deletion/account-deletion').then((m) => m.AccountDeletion),
  },
  {
    // Adresse dédiée déclarée aux stores comme politique de confidentialité.
    // Un `?tab=privacy` suffirait techniquement, mais un paramètre de requête se
    // perd trop facilement (copie tronquée, normalisation d'URL) et le visiteur
    // atterrirait alors sur les CGU. Ici l'onglet est porté par le chemin.
    path: 'confidentialite',
    redirectTo: () => inject(Router).createUrlTree(['/legal'], { queryParams: { tab: 'privacy' } }),
  },
  {
    // Hors authentification : les documents doivent rester lisibles depuis
    // l'écran de connexion, avant même qu'un compte existe.
    path: 'legal',
    loadComponent: () => import('./features/more/pages/legal/legal').then((m) => m.Legal),
  },
  {
    path: '**',
    redirectTo: 'home',
  },
];
